/**
 * INV-04 · Uma única definição de "qual produto o visitante está testando".
 *
 * Desde 18/08/2026 o site vende dois produtos, e a escolha acontece ANTES do
 * cadastro — no modal da home, ou implicitamente ao entrar por `/agendamento` /
 * `/atendimento`. Essa escolha atravessa quatro camadas:
 *
 *   1. a URL de cadastro .......... `/login?tab=signup&produto=atendimento`
 *   2. o corpo do signup .......... `POST /auth/signup { produto }`
 *   3. o backend .................. `resolveTrialPlanName` → FREE | FREE_ATENDIMENTO
 *   4. o GA4 ...................... dimensão `product` do `trial_cta_click`
 *
 * As quatro precisam falar a MESMA string. Um `"atendimento"` digitado à mão em
 * qualquer uma delas não daria erro em lugar nenhum: o backend cairia no default
 * (agendamento) e entregaria um trial COM agenda a quem pediu o sem. Falha
 * silenciosa, do mesmo formato do bug de timezone — por isso o literal mora aqui,
 * e só aqui.
 *
 * ⚠️ O espelho no backend é `backend/src/constants/trial.ts` (`TRIAL_PRODUCTS`).
 * Mexeu num, mexa no outro.
 */

/** Os dois produtos vendidos. Os valores são o contrato com o backend. */
export const TRIAL_PRODUCTS = Object.freeze({
  AGENDAMENTO: "agendamento",
  ATENDIMENTO: "atendimento",
});

export const TRIAL_PRODUCT_VALUES = Object.freeze(
  Object.values(TRIAL_PRODUCTS),
);

/** Nome do parâmetro na URL de cadastro. Em pt-BR, como as rotas públicas. */
export const TRIAL_PRODUCT_PARAM = "produto";

/**
 * `true` só para um dos dois valores do enum.
 *
 * Usa `includes` sobre a lista congelada — e não um objeto indexado — para que
 * `"__proto__"`, `"constructor"` e afins não encontrem propriedade herdada do
 * prototype e passem como válidos.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isTrialProduct(value) {
  return TRIAL_PRODUCT_VALUES.includes(value);
}

/**
 * Monta o caminho de cadastro carregando o produto escolhido.
 *
 * Produto ausente ou inválido devolve o caminho SEM o parâmetro, em vez de
 * inventar um default: quem chega assim é quem ainda não escolheu, e o backend
 * já trata essa ausência (cai em agendamento, o comportamento histórico).
 * Escrever um default aqui esconderia do GA4 a diferença entre "escolheu
 * agendamento" e "não escolheu nada" — que é justamente o que se quer medir.
 *
 * @param {string} [product] - Valor do enum, ou nada.
 * @returns {string} Ex.: `/login?tab=signup&produto=atendimento`
 */
export function buildTrialSignupPath(product) {
  const base = "/login?tab=signup";
  return isTrialProduct(product)
    ? `${base}&${TRIAL_PRODUCT_PARAM}=${product}`
    : base;
}

/**
 * Rota pública → produto que aquela página vende.
 *
 * O CTA do cabeçalho é o mesmo em todo o site, mas o visitante não: quem clica
 * em "Testar grátis" lendo `/atendimento` está pedindo o Atendimento, e mandá-lo
 * para o trial de Agendamento seria contrariar o que ele acabou de ler.
 *
 * As 5 páginas de segmento vendem AGENDA (cliente marca hora), então apontam
 * para Agendamento.
 *
 * Rota que não vende produto nenhum — `/`, `/recursos`, institucionais — devolve
 * `undefined` DE PROPÓSITO: ali o visitante ainda não escolheu, e é o modal (ou
 * o default do backend) que resolve. Chutar um produto aqui apagaria do GA4 a
 * diferença entre "escolheu" e "não escolheu".
 */
const PATH_TO_PRODUCT = Object.freeze({
  "/agendamento": TRIAL_PRODUCTS.AGENDAMENTO,
  "/atendimento": TRIAL_PRODUCTS.ATENDIMENTO,
  "/barbearias": TRIAL_PRODUCTS.AGENDAMENTO,
  "/consultorios": TRIAL_PRODUCTS.AGENDAMENTO,
  "/mecanicas": TRIAL_PRODUCTS.AGENDAMENTO,
  "/lava-jatos": TRIAL_PRODUCTS.AGENDAMENTO,
  "/academias": TRIAL_PRODUCTS.AGENDAMENTO,
});

/**
 * @param {string} pathname
 * @returns {string|undefined} Produto da rota, ou `undefined` se ela não vende um.
 */
export function resolveProductFromPath(pathname) {
  if (typeof pathname !== "string") return undefined;

  let path = pathname.split("?")[0].split("#")[0].trim().toLowerCase();
  while (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);

  // `hasOwnProperty` pelo mesmo motivo do `maps.js` do analytics: sem ele uma
  // rota `/constructor` acharia propriedade herdada do prototype.
  return Object.prototype.hasOwnProperty.call(PATH_TO_PRODUCT, path)
    ? PATH_TO_PRODUCT[path]
    : undefined;
}

/**
 * Chave do `sessionStorage` onde o produto escolhido espera entre o retorno do
 * OAuth e o submit do onboarding.
 *
 * O produto chega no fragmento da URL de `/auth-success` e só é consumido lá
 * adiante, no `POST /tenants/setup`. Precisa de algum lugar para atravessar essa
 * navegação — e esse lugar é `sessionStorage`, não `localStorage`:
 *
 * - **Não é chave de sessão** (INV-F2). Não identifica nem autentica ninguém: é
 *   a lembrança de um clique num botão de marketing. Por isso NÃO entra em
 *   `SESSION_STORAGE_KEYS`, e `clearSession()` não a conhece.
 * - `sessionStorage` morre com a aba, que é exatamente o tempo de vida do
 *   fluxo "voltei do Google → escolhi meu negócio". Em `localStorage` ela
 *   sobreviveria a logout e a troca de conta, e o próximo cadastro feito
 *   naquele navegador herdaria o produto de outro cadastro — falha silenciosa
 *   do mesmo formato que este módulo existe para impedir.
 */
export const TRIAL_PRODUCT_SESSION_KEY = "nx_trial_produto";

/**
 * Guarda o produto para o submit do onboarding. Valor inválido não escreve nada
 * (e limpa resíduo anterior): melhor ausência — que o backend trata com o
 * default histórico — do que um produto inventado.
 *
 * try/catch defensivo, no padrão de `clearSession()`: `sessionStorage` pode
 * estar indisponível em modo privado. Falha silenciosa é preferível a quebrar
 * o login.
 *
 * @param {unknown} product
 */
export function rememberTrialProduct(product) {
  try {
    if (isTrialProduct(product)) {
      window.sessionStorage.setItem(TRIAL_PRODUCT_SESSION_KEY, product);
    } else {
      window.sessionStorage.removeItem(TRIAL_PRODUCT_SESSION_KEY);
    }
  } catch {
    // sessionStorage indisponível — seguir sem lembrar o produto.
  }
}

/**
 * Lê o produto guardado. Devolve `undefined` quando não há nada, quando o
 * storage está indisponível ou quando o valor guardado não é do enum.
 *
 * @returns {string|undefined}
 */
export function readTrialProduct() {
  try {
    const stored = window.sessionStorage.getItem(TRIAL_PRODUCT_SESSION_KEY);
    return isTrialProduct(stored) ? stored : undefined;
  } catch {
    return undefined;
  }
}

/** Descarta o produto guardado — chamado após o onboarding concluir. */
export function forgetTrialProduct() {
  try {
    window.sessionStorage.removeItem(TRIAL_PRODUCT_SESSION_KEY);
  } catch {
    // sessionStorage indisponível — ignorar.
  }
}
